import { type InferResponseType, unwrap, withSignal } from "@nexxt/api-client";
import type { OrderConfirmationAnswerBody } from "@nexxt/contracts/sales";
import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api";
import { orderQueries } from "./orders.queries";

// Server state of the confirmation of an order (B-47, ADR-208, ADR-221): its revisions, kept under the order's key,
// and the customer's answer, after which the order and the lists of the home are read again (the one to accept
// leaves its section).

const ofOrder = api.sales.orders[":salesOrderId"].confirmations;
const answerRoute = api.sales["order-confirmations"][":confirmationId"].answer;

export type CustomerConfirmation = InferResponseType<typeof ofOrder.$get, 200>[number];

export const confirmationQueries = {
  /** The revisions the customer received, the latest first. */
  ofOrder: (salesOrderId: string) =>
    queryOptions({
      queryKey: [...orderQueries.of(salesOrderId), "confirmations"],
      queryFn: ({ signal }) => unwrap(ofOrder.$get({ param: { salesOrderId } }, withSignal(signal))),
    }),
};

/**
 * `sales.orderConfirmation.decide`: once per revision. Whatever the answer, the revisions are read again: a refusal
 * (a newer revision, a colleague who answered first) shows what is there now.
 */
export function useAnswerConfirmation(salesOrderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ confirmationId, answer: json }: { confirmationId: string; answer: OrderConfirmationAnswerBody }) =>
      unwrap(answerRoute.$post({ param: { confirmationId }, json })),
    onSuccess: (revision) =>
      qc.setQueryData(confirmationQueries.ofOrder(salesOrderId).queryKey, (revisions) =>
        revisions?.map((r) => (r.id === revision.id ? revision : r)),
      ),
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: orderQueries.of(salesOrderId) }),
        qc.invalidateQueries({ queryKey: orderQueries.lists }),
      ]),
  });
}
